import React, { useState, useContext } from 'react';
import {
  Divider,
  Flex,
  Modal,
  Typography,
  Button,
  Form,
  Input,
  Col,
  Row,
  Select,
} from 'antd';
import Icon from '@ant-design/icons';
import { useTheme } from '../../../../context/ThemeContext';
import { ReactComponent as SOSAlertWhiteSvg } from '../../../../assets/icons/sos-alert-white.svg';
import { ReactComponent as SOSAlertBlackSvg } from '../../../../assets/icons/sos-alert-black.svg';
import { ReactComponent as CancelSvg } from '../../../../assets/icons/cancel.svg';
import { ReactComponent as CancelBlackSvg } from '../../../../assets/icons/cancel-black.svg';
import { ErrorMessageContext } from '../../../../context/ErrorMessageContext';
import { DARK } from '../../../../utilities/constants';
import { ReactComponent as SelectDropdownSvg } from '../../../../assets/icons/select-dropdown.svg';
import { ReactComponent as SelectDropdownBlackSvg } from '../../../../assets/icons/select-dropdown-black.svg';
import CustomFormDatePicker from '../../../../components/CustomFormDatePicker/CustomFormDatePicker';
import { API } from '../../../../services';
import DescriptionFormItem from '../../../../components/DescriptionFormItem/DescriptionFormItem';
import {
  sanitizeInput,
  getErrorMessage,
} from '../../../../utilities/commonFunctions';
import PropTypes from 'prop-types';
import EmptyCard from '../../../../components/EmptyCard/EmptyCard';

const { Text } = Typography;

const callStatusList = [
  { value: 'False Alert', label: 'False Alert' },
  { value: 'Genuine call', label: 'Genuine call' },
];

const sosTypeList = [
  { value: 'Medical emergency', label: 'Medical emergency' },
  { value: 'Road accident', label: 'Road accident' },
  { value: 'Fight', label: 'Fight' },
  { value: 'Others', label: 'Others' },
];

const SOTicketModal = ({ isModalOpen, setIsModalOpen, onCreateSuccess }) => {
  const { theme } = useTheme();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const { addErrorMessage } = useContext(ErrorMessageContext);

  const onFinish = async values => {
    try {
      setLoading(true);
      const payload = {
        so_ticket_id: sanitizeInput(values?.so_ticket_id),
        description: sanitizeInput(values?.description),
        assigned_to: sanitizeInput(values?.assigned_to) || '',
        reporter: sanitizeInput(values?.reporter),
        sos_type: values?.sos_type,
        call_type: values?.call_type,
        latitude: Number.parseFloat(sanitizeInput(values?.latitude)),
        longitude: Number.parseFloat(sanitizeInput(values?.longitude)),
        event_timestamp: sanitizeInput(values?.event_timestamp)?.format(
          'YYYY-MM-DDTHH:mm:ss'
        ),
      };
      const response = await API.createSoTicket(payload);
      if (response?.status === 201) {
        addErrorMessage('SO Ticket created successfully', 'success');
        onCreateSuccess?.();
        form.resetFields();
        setIsModalOpen(false);
      }
    } catch (error) {
      addErrorMessage(
        getErrorMessage(
          error,
          'Unable to create ticket! Please try again after sometime.'
        )
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setIsModalOpen(false);
  };

  return (
    <Modal
      centered
      open={isModalOpen}
      destroyOnHidden
      title={
        <Flex vertical>
          <Flex className="broadcast-modal-title" justify="space-between">
            <Flex gap={8}>
              <Icon
                component={theme === DARK ? SOSAlertWhiteSvg : SOSAlertBlackSvg}
              />
              <Text>Create SO Ticket</Text>
            </Flex>
            <Icon
              component={theme === DARK ? CancelSvg : CancelBlackSvg}
              onClick={handleCancel}
            />
          </Flex>
          <Divider />
        </Flex>
      }
      footer={[
        <Button
          key="send"
          type="primary"
          htmlType="submit"
          loading={loading}
          disabled={loading}
          onClick={() => form.submit()}
        >
          Create
        </Button>,
        <Button key="cancel" onClick={handleCancel} disabled={loading}>
          Cancel
        </Button>,
      ]}
      className="broadcast-modal"
      closable={false}
      width="40%"
    >
      <Form
        form={form}
        onFinish={onFinish}
        layout="vertical"
        className="full-height broadcast-form"
        scrollToFirstError
        requiredMark={false}
      >
        <Row
          className="full-width"
          gutter={[
            { xs: 0, lg: 24 },
            { xs: 24, lg: 24 },
          ]}
        >
          <Col xs={24} lg={12} style={{ paddingLeft: 0 }}>
            <Form.Item
              name="reporter"
              rules={[
                {
                  required: true,
                  message: 'Please enter reporter',
                },
              ]}
              label={<Text strong>Reporter</Text>}
            >
              <Input autoSize placeholder="e.g: John Doe" />
            </Form.Item>
          </Col>
        </Row>
        <Row
          className="full-width"
          gutter={[
            { xs: 0, lg: 24 },
            { xs: 24, lg: 24 },
          ]}
        >
          <Col xs={24} lg={12} style={{ paddingLeft: 0 }}>
            <Form.Item
              name="call_type"
              rules={[
                {
                  required: true,
                  message: 'Please enter call type',
                },
              ]}
              label={
                <Text strong style={{ marginTop: '4px' }}>
                  Call Type
                </Text>
              }
            >
              <Select
                placeholder="--select--"
                options={callStatusList}
                size="large"
                className="custom-select-dropdown"
                suffixIcon={
                  <Icon
                    component={
                      theme === DARK
                        ? SelectDropdownSvg
                        : SelectDropdownBlackSvg
                    }
                    style={{
                      pointerEvents: 'none',
                    }}
                  />
                }
                notFoundContent={<EmptyCard />}
              />
            </Form.Item>
          </Col>
          <Col xs={24} lg={12} style={{ paddingRight: 0 }}>
            <Form.Item
              name="sos_type"
              rules={[
                {
                  required: true,
                  message: 'Please enter SOS Type',
                },
              ]}
              label={
                <Text strong style={{ marginTop: '4px' }}>
                  SOS Type
                </Text>
              }
            >
              <Select
                placeholder="--select--"
                options={sosTypeList}
                size="large"
                className="custom-select-dropdown"
                suffixIcon={
                  <Icon
                    component={
                      theme === DARK
                        ? SelectDropdownSvg
                        : SelectDropdownBlackSvg
                    }
                    style={{
                      pointerEvents: 'none',
                    }}
                  />
                }
                notFoundContent={<EmptyCard />}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row
          className="full-width"
          gutter={[
            { xs: 0, lg: 24 },
            { xs: 24, lg: 24 },
          ]}
        >
          <Col xs={24} lg={12} style={{ paddingLeft: 0 }}>
            <Form.Item
              name="so_ticket_id"
              rules={[
                {
                  required: true,
                  message: 'Please enter SO Ticket ID',
                },
              ]}
              label={
                <Text strong style={{ marginTop: '4px' }}>
                  SO Ticket ID
                </Text>
              }
            >
              <Input
                autoSize
                placeholder="e.g: 96e28a0e-d4e3"
                className="input-text"
              />
            </Form.Item>
          </Col>
          <Col xs={24} lg={12} style={{ paddingRight: 0, paddingTop: 36 }}>
            <CustomFormDatePicker
              width="100%"
              name="event_timestamp"
              rules={[
                {
                  required: true,
                  message: 'Please select SOS Timestamp',
                },
              ]}
              label="SOS Timestamp"
              showTime={true}
            />
          </Col>
        </Row>
        <Row
          className="full-width"
          gutter={[
            { xs: 0, lg: 24 },
            { xs: 24, lg: 24 },
          ]}
        >
          <Col xs={24} lg={12} style={{ paddingLeft: 0 }}>
            <Form.Item
              name="latitude"
              rules={[
                {
                  required: true,
                  message: 'Please enter latitude',
                },
                {
                  validator: (_, value) => {
                    if (value === undefined || value === null || value === '') {
                      return Promise.resolve();
                    }
                    const latitude = Number.parseFloat(value);
                    if (latitude >= -90 && latitude <= 90) {
                      return Promise.resolve();
                    }
                    return Promise.reject(
                      new Error(
                        'Latitude must be between -90 and 90 (inclusive)'
                      )
                    );
                  },
                },
              ]}
              label={
                <Text strong style={{ marginTop: '4px' }}>
                  Latitude
                </Text>
              }
            >
              <Input
                autoSize
                placeholder="e.g: 12.94068604"
                className="input-text"
                value={Float32Array}
              />
            </Form.Item>
          </Col>
          <Col xs={24} lg={12} style={{ paddingRight: 0 }}>
            <Form.Item
              name="longitude"
              rules={[
                {
                  required: true,
                  message: 'Please enter longitude',
                },
                {
                  validator: (_, value) => {
                    if (value === undefined || value === null || value === '') {
                      return Promise.resolve();
                    }
                    const latitude = Number.parseFloat(value);
                    if (latitude >= -180 && latitude < 180) {
                      return Promise.resolve();
                    }
                    return Promise.reject(
                      new Error(
                        'Longitude must be between -180 and 180 (-180 inclusive)'
                      )
                    );
                  },
                },
              ]}
              label={
                <Text strong style={{ marginTop: '4px' }}>
                  Longitude
                </Text>
              }
            >
              <Input
                autoSize
                placeholder="e.g: 77.60560710"
                className="input-text"
                value={Float32Array}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row className="full-width">
          <DescriptionFormItem />
        </Row>
        <Row
          className="full-width"
          gutter={[
            { xs: 0, lg: 24 },
            { xs: 24, lg: 24 },
          ]}
        >
          <Col xs={24} lg={12} style={{ paddingLeft: 0 }}>
            <Form.Item
              name="assigned_to"
              label={
                <Text strong style={{ marginTop: '4px' }}>
                  Assign to
                </Text>
              }
            >
              <Input
                autoSize
                placeholder="e.g: John Doe"
                className="input-text"
              />
            </Form.Item>
            <Text style={{ fontSize: '12px' }}>{`(optional)`}</Text>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

SOTicketModal.propTypes = {
  isModalOpen: PropTypes.bool.isRequired,
  setIsModalOpen: PropTypes.func.isRequired,
  onCreateSuccess: PropTypes.func,
};

export default React.memo(SOTicketModal);
